import clsx from 'clsx'
import { equals, filter, union } from 'ramda'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'

import { useCompanies } from 'api/querries/filters/useCompanies'
import { useDepartments } from 'api/querries/filters/useDepartments'
import { useJobFamily } from 'api/querries/filters/useJobFamily'
import { useClusters } from 'api/querries/useClusters'
import { useCommunityTags } from 'api/querries/useCommunityTags'
import { useExperiences } from 'api/querries/useExperiences'
import { useSkills } from 'api/querries/useSkills'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import { ReactComponent as Favourites } from 'assets/icons/favourites.svg'
import { ReactComponent as MoreIcon } from 'assets/icons/more.svg'
import { ReactComponent as SearchIcon } from 'assets/icons/search-icon.svg'
import { Button } from 'components/common/button/Button'
import { Divider } from 'components/uikit/Divider'
import { Icon } from 'components/uikit/Icon/Icon'
import { ListItem } from 'components/uikit/ListItem'
import MultiSelect from 'components/uikit/MultiSelect'
import { SideModal } from 'components/uikit/SideModal'
import SingleSelect from 'components/uikit/SingleSelect'
import { AnalyticsActionType } from 'types/analytics.type'
import { CommunityTag } from 'types/community-tags'
import { ANALYTICS_EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './PractitionersListFilters.module.scss'

export interface PractitionersFiltersType {
  clusterId?: string[]
  communityTags?: CommunityTag[]
  onlyFavourites?: boolean
  company?: string
  // department
  jobFamilyName?: string
  // jobFamily
  jobFamilyGroup?: string
  skills?: string[]
  experiences?: string[]
  search?: string
}
export const DEFAULT_SIDEBAR_FILTERS = {
  company: undefined,
  jobFamilyName: undefined,
  jobFamilyGroup: undefined,
  skills: [],
  experiences: [],
}

export const DEFAULT_FILTERS: PractitionersFiltersType = {
  clusterId: [],
  communityTags: [],
  onlyFavourites: false,
  search: undefined,
  ...DEFAULT_SIDEBAR_FILTERS,
}

interface ChangeEvent {
  detail?: { value?: any }
  target?: { value?: any }
}
const PractitionersListFilters = forwardRef(
  ({ onChange }: { onChange: (value: Partial<PractitionersFiltersType>) => void }, ref) => {
    const [filters, setFilters] = useState<PractitionersFiltersType | undefined>(DEFAULT_FILTERS)
    const [search, setSearch] = useState<string | undefined>()
    const [sidebarFilters, setSidebarFilters] = useState(DEFAULT_SIDEBAR_FILTERS)

    const [isVisible, setIsVisible] = useState(false)

    const { data: clusters } = useClusters()
    const { data: communityTags } = useCommunityTags()
    const { data: skills } = useSkills()
    const { data: experiences } = useExperiences()
    const { data: companies } = useCompanies()
    const { data: jobFamily } = useJobFamily()
    const { data: departments } = useDepartments()

    const handleSearchChange = (e?: React.ChangeEvent<HTMLInputElement>) => {
      const value = e?.target?.value
      setSearch(value)

      setFilters(prevFilters => ({
        ...prevFilters,
        search: value,
      }))
      trackAnalytics({
        type: AnalyticsActionType.action,
        payload: {
          action: ANALYTICS_EVENTS.PRACTITIONERS_FILTERS_SEARCH,
          params: [
            {
              key: 'search',
              value: value ?? '',
            },
          ],
        },
      })
    }

    const handleSelectFilter = (key: keyof PractitionersFiltersType, event: ChangeEvent) => {
      const selectedValues = event?.detail?.value || undefined
      setFilters(prevFilters => ({
        ...prevFilters,
        [key]: selectedValues,
      }))
    }

    const handleSelectSidebarFilter = (key: keyof PractitionersFiltersType, event: ChangeEvent) => {
      const selectedValues = event?.detail?.value || []
      setSidebarFilters(prevFilters => ({
        ...prevFilters,
        [key]: selectedValues,
      }))
    }

    const handleApplySidebarFilters = () => {
      setFilters(prevFilters => ({ ...prevFilters, ...sidebarFilters }))
      setIsVisible(false)
      trackAnalytics({
        type: AnalyticsActionType.action,
        payload: {
          action: ANALYTICS_EVENTS.PRACTITIONERS_SIDEBAR_FILTERS_APPLIED,
          params: [
            {
              key: 'values',
              value: JSON.stringify(sidebarFilters),
            },
          ],
        },
      })
    }

    const handleCancelSidebarFilters = () => {
      setSidebarFilters(DEFAULT_SIDEBAR_FILTERS)
      setFilters(DEFAULT_FILTERS)
      setIsVisible(false)
    }

    //eslint-disable-next-line
    useEffect(() => onChange(filters || {}), [filters])

    const clearAllFilters = () => {
      setFilters(DEFAULT_FILTERS)
      setSidebarFilters(DEFAULT_SIDEBAR_FILTERS)
      setSearch('')
    }

    useImperativeHandle(ref, () => ({
      clearAllFilters,
    }))

    const countDirtyFileds = countDifferences(DEFAULT_SIDEBAR_FILTERS, sidebarFilters)

    return (
      <div className={styles.filtersWrapper}>
        <div className={clsx(styles.searchWrapper, 'w-full')}>
          <Icon icon={SearchIcon} aria-label="Search icon" className={styles.searchIcon} color="#8b919a" />

          <input
            name="search"
            placeholder="Search for a practitioner"
            value={search}
            className={clsx(styles.input, 'w-full')}
            onChange={handleSearchChange}
          />
          {search && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => {
                setSearch('')
                setFilters(prevFilters => ({ ...prevFilters, search: undefined }))
              }}
              aria-label="Clear search"
            >
              <Icon icon={CloseIcon} aria-label="Clear icon" color="#8b919a" />
            </button>
          )}
        </div>
        <MultiSelect
          className={styles.select}
          placeholder="Select OU Cluster"
          hideSearch={true}
          value={filters?.clusterId}
          onChange={e => {
            handleSelectFilter('clusterId', e)
            trackAnalytics({
              type: AnalyticsActionType.action,
              payload: {
                action: ANALYTICS_EVENTS.PRACTITIONERS_FILTERS_OU_SELECTED,
                params: [
                  {
                    key: 'input',
                    value: (e.target.value as any[])?.join(',') ?? '',
                  },
                ],
              },
            })
          }}
        >
          {clusters?.map((cluster, index) => (
            <ListItem value={cluster?.id} key={index}>
              <p slot="label">{cluster?.name}</p>
            </ListItem>
          ))}
        </MultiSelect>
        <MultiSelect
          placeholder="Select Community Tags"
          hideSearch={true}
          value={filters?.communityTags}
          onChange={e => {
            handleSelectFilter('communityTags', e)
            trackAnalytics({
              type: AnalyticsActionType.action,
              payload: {
                action: ANALYTICS_EVENTS.PRACTITIONERS_FILTERS_CT_SELECTED,
                params: [
                  {
                    key: 'input',
                    value: (e.target.value as any[])?.join(',') ?? '',
                  },
                ],
              },
            })
          }}
        >
          {communityTags?.map((tag, index) => (
            <ListItem key={index} value={tag}>
              <p slot="label">{tag?.name}</p>
            </ListItem>
          ))}
        </MultiSelect>
        <div className="flex justify-end">
          <button
            className={clsx(
              filters?.onlyFavourites ? 'bg-black border-[#ffffff]' : 'bg-transparent border-[#8B8B8B]',
              'hover:cursor-pointer z-1 border border-solid  rounded-full flex flex-row items-center justify-center min-w-10 w-10 h-10 transition-all',
            )}
            onClick={() => {
              setFilters(prevFilters => ({ ...prevFilters, onlyFavourites: !prevFilters?.onlyFavourites }))
              trackAnalytics({
                type: AnalyticsActionType.action,
                payload: {
                  action: ANALYTICS_EVENTS.PRACTITIONERS_FILTERS_FAVORITE_TOGGLE_CLICKED,
                  params: [
                    {
                      key: 'toggle',
                      value: filters?.onlyFavourites ? 'on' : 'off',
                    },
                  ],
                },
              })
            }}
          >
            <Icon icon={Favourites} color={filters?.onlyFavourites ? 'white' : '#4C4C4C'} />
          </button>
        </div>
        <div>
          <div>
            <button
              onClick={() => setIsVisible(true)}
              className={clsx(
                'bg-transparent hover:cursor-pointer z-1 border border-solid border-[#8B8B8B] rounded-full flex flex-row items-center justify-center h-10 px-3',
              )}
            >
              <Icon icon={MoreIcon} size={16} color="#4C4C4C" className="mr-[10px]" />
              <span className={clsx(styles.SStrong, 'text-[#383838] whitespace-nowrap')}>
                More filters <span className={clsx(styles.SBody, 'text-[#383838]')}>({countDirtyFileds})</span>
              </span>
            </button>
            <SideModal
              open={isVisible}
              className={styles.filtersSideModal}
              onModalClose={() => {
                setSidebarFilters(DEFAULT_SIDEBAR_FILTERS)
                setFilters(prevFilters => ({ ...prevFilters, ...DEFAULT_SIDEBAR_FILTERS }))
                setIsVisible(false)
              }}
            >
              <div slot="header" className={clsx(styles.filtersHeading)}>
                More filters
              </div>
              <div slot="body" className="tour-openblueprint-sortandfilter pb-4">
                <div>
                  <SingleSelect
                    className={styles.select}
                    hideSearch={true}
                    placeholder="Company"
                    value={sidebarFilters?.company}
                    labelConfig={{
                      text: 'Company',
                    }}
                    required
                    onChange={ev => {
                      handleSelectSidebarFilter('company', ev)
                    }}
                  >
                    {companies?.map(company => (
                      <ListItem key={company.id} value={company.name}>
                        <p slot="label" className={styles.SBody}>
                          {company.name}
                        </p>
                      </ListItem>
                    ))}
                  </SingleSelect>
                </div>
                <div className="mt-[16px]">
                  <SingleSelect
                    className={styles.select}
                    placeholder="Department"
                    value={sidebarFilters?.jobFamilyName}
                    labelConfig={{
                      text: 'Department',
                    }}
                    required
                    onChange={ev => {
                      handleSelectSidebarFilter('jobFamilyName', ev)
                    }}
                  >
                    {departments?.map(depratment => (
                      <ListItem key={depratment.id} value={depratment.name}>
                        <p slot="label" className={styles.SBody}>
                          {depratment.name}
                        </p>
                      </ListItem>
                    ))}
                  </SingleSelect>
                </div>
                <div className="mt-[16px]">
                  <SingleSelect
                    hideSearch={true}
                    className={styles.select}
                    placeholder="Job Family"
                    value={sidebarFilters?.jobFamilyGroup}
                    labelConfig={{
                      text: 'Job Family',
                    }}
                    required
                    onChange={ev => {
                      handleSelectSidebarFilter('jobFamilyGroup', ev)
                    }}
                  >
                    {jobFamily?.map(jobFamily => (
                      <ListItem key={jobFamily.id} value={jobFamily.name}>
                        <p slot="label" className={styles.SBody}>
                          {jobFamily.name}
                        </p>
                      </ListItem>
                    ))}
                  </SingleSelect>
                </div>
                <div>
                  <Divider className="my-[16px]" />
                </div>

                <div className="mt-[16px]">
                  <MultiSelect
                    className={styles.select}
                    placeholder="Skills"
                    value={sidebarFilters?.skills}
                    labelConfig={{
                      text: 'Skills Level 4 or Above',
                    }}
                    required
                    onChange={ev => {
                      handleSelectSidebarFilter('skills', ev)
                    }}
                  >
                    {skills?.map((brand: any) => (
                      <ListItem key={brand.id} value={brand.id}>
                        <p slot="label" className={styles.SBody}>
                          {brand.name}
                        </p>
                      </ListItem>
                    ))}
                  </MultiSelect>
                </div>
                <div className="mt-[16px]">
                  <MultiSelect
                    className={styles.select}
                    placeholder="Experiences"
                    value={sidebarFilters?.experiences}
                    labelConfig={{
                      text: 'Experiences Level 4 or Above',
                    }}
                    required
                    onChange={ev => {
                      handleSelectSidebarFilter('experiences', ev)
                    }}
                  >
                    {experiences?.map((brand: any) => (
                      <ListItem key={brand.id} value={brand.id}>
                        <p slot="label" className={styles.SBody}>
                          {brand?.name}
                        </p>
                      </ListItem>
                    ))}
                  </MultiSelect>
                </div>
              </div>
              <div slot="actions">
                <div className="flex flex-row items-center justify-end">
                  <Button text="Cancel" variant="primary" onClick={handleCancelSidebarFilters} />
                  <Button text="Apply filters" variant="secondary" onClick={handleApplySidebarFilters} />
                </div>
              </div>
            </SideModal>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            className={clsx(styles.btnClearAll, 'text-[#383838] h-10 hover:cursor-pointer ml-3 whitespace-nowrap')}
            onClick={clearAllFilters}
          >
            Clear All
          </button>
        </div>
      </div>
    )
  },
)

export default PractitionersListFilters

const countDifferences = (original: PractitionersFiltersType, modified?: PractitionersFiltersType): number => {
  // Get all unique keys from both objects

  if (!modified) return 0

  const keys = union(Object.keys(original), Object.keys(modified))

  // Filter keys where the values are different
  const differingKeys = filter(
    key => !equals(original[key as keyof PractitionersFiltersType], modified[key as keyof PractitionersFiltersType]),
    keys,
  )

  // Return the number of differing keys
  return differingKeys.length
}
